import React, { ReactNode, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { trpc } from '../../api/trpc';
import { Carousel, CarouselItem } from '../../atoms/carousel';
import { DialogClose } from '../../atoms/dialog-close';
import styled from '@emotion/styled';

const featureNotifications = new URL(
  '../../static/premium/feature-notifications.png',
  import.meta.url
);
const featureCharts = new URL(
  '../../static/premium/feature-charts.png',
  import.meta.url
);
const featureSchedule = new URL(
  '../../static/premium/feature-schedule.png',
  import.meta.url
);
const featureSpot = new URL(
  '../../static/premium/feature-spot.png',
  import.meta.url
);
const featureReports = new URL(
  '../../static/premium/feature-reports.png',
  import.meta.url
);

const FeaturesImageCarousel = styled(Box)`
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  animation: fadeImages 15s infinite;

  @keyframes fadeImages {
    0%,
    18% {
      background-image: url(${featureNotifications.toString()});
      opacity: 1;
    }
    20% {
      background-image: url(${featureCharts.toString()});
      opacity: 1;
    }
    21%,
    38% {
      background-image: url(${featureCharts.toString()});
      opacity: 1;
    }
    40% {
      background-image: url(${featureSchedule.toString()});
      opacity: 1;
    }
    41%,
    58% {
      background-image: url(${featureSchedule.toString()});
      opacity: 1;
    }
    60% {
      background-image: url(${featureSpot.toString()});
      opacity: 1;
    }
    61%,
    78% {
      background-image: url(${featureSpot.toString()});
      opacity: 1;
    }
    80% {
      background-image: url(${featureReports.toString()});
      opacity: 1;
    }
    81%,
    98% {
      background-image: url(${featureReports.toString()});
      opacity: 1;
    }
    100% {
      background-image: url(${featureNotifications.toString()});
      opacity: 1;
    }
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const PremiumDialog = ({ open, onClose }: Props) => {
  const { mutateAsync: getSubscribeLink } =
    trpc.subscriptions.getSubscribeLink.useMutation();

  const onSubscribe = async () => {
    try {
      const { link } = await getSubscribeLink({ url: window.location.href });
      window.location.href = link;
    } catch (error) {
      alert(
        'Došlo k chybě při získávání odkazu na předplatné. Zkuste to prosím znovu později.'
      );
    }
  };

  const items: {
    subtitle: ReactNode;
    content: ReactNode;
    imageSrc?: string;
    imageSx?: SxProps;
    imageComponent?: ReactNode;
  }[] = [
    {
      subtitle: (
        <FormattedMessage
          id="premium.dialog.notifications.title"
          defaultMessage="Notifikace"
        />
      ),
      content: (
        <FormattedMessage
          id="premium.dialog.notifications.subtitle"
          defaultMessage="Získávejte notifikace o záporných cenách, nabité baterii a dalších událostech"
        />
      ),
      imageSrc: featureNotifications.toString(),
    },
    {
      subtitle: (
        <FormattedMessage
          id="premium.dialog.charts.title"
          defaultMessage="Detailní grafy"
        />
      ),
      content: (
        <FormattedMessage
          id="premium.dialog.charts.subtitle"
          defaultMessage="Získejte možnost sledovat výkon jednotlivých stringů, spotřebu dle fází a další podrobné informace."
        />
      ),
      imageSrc: featureCharts.toString(),
    },
    {
      subtitle: (
        <FormattedMessage
          id="premium.dialog.schedule.title"
          defaultMessage="Vylepšený plánovací kalendář"
        />
      ),
      content: (
        <FormattedMessage
          id="premium.dialog.schedule.subtitle"
          defaultMessage="Naplánujte si ručně nabíjení či vybíjení baterie dle potřeby"
        />
      ),
      imageSrc: featureSchedule.toString(),
    },
    {
      subtitle: (
        <FormattedMessage
          id="premium.dialog.reports.title"
          defaultMessage="Pokročilé reporty"
        />
      ),
      content: (
        <FormattedMessage
          id="premium.dialog.reports.subtitle"
          defaultMessage="Detailní měsíční reporty, které vám poskytnou jasný obraz o výrobě, spotřebě a využití energie ve vaší domácnosti. Všechny důležité údaje máte na jednom místě - srozumitelně, přehledně a doplněné grafy."
        />
      ),
      imageSrc: featureReports.toString(),
      imageSx: {},
    },
    {
      subtitle: (
        <FormattedMessage
          id="premium.dialog.spot.title"
          defaultMessage="Historie spotových cen"
        />
      ),
      content: (
        <FormattedMessage
          id="premium.dialog.spot.subtitle"
          defaultMessage="Získtejte možnost nahlédnout do historie spotových cen a plánů, které Infigy v minulosti připravilo"
        />
      ),
      imageSrc: featureSpot.toString(),
    },
    {
      subtitle: (
        <FormattedMessage
          id="premium.dialog.other_benefits.title"
          defaultMessage="Další benefity přijdou brzy"
        />
      ),
      content: (
        <FormattedMessage
          id="premium.dialog.other_benefits.subtitle"
          defaultMessage="Připravujeme spoustu dalších benefitů, které budeme postupně uvolňovat pro předplatitele Infigy Plus"
        />
      ),
      imageComponent: (
        <FeaturesImageCarousel
          sx={{
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            width: '100%',
            height: 300,
            mt: 4,
            mb: 3,
            mx: 'auto',
          }}
        />
      ),
    },
  ];

  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={!sm}
        slotProps={{ paper: { sx: { bgcolor: 'secondary.main' } } }}
      >
        <DialogClose onClose={onClose} />

        <DialogContent sx={{}}>
          <Carousel
            items={items}
            sx={{ pt: { xs: 1, md: 0 } }}
            renderItem={({ item, isSnapPoint }) => (
              <CarouselItem key={item.imageSrc} isSnapPoint={isSnapPoint}>
                <Typography variant="h2" sx={{ textAlign: 'center', mt: 2 }}>
                  Infigy Plus
                </Typography>

                {item.imageComponent ? (
                  item.imageComponent
                ) : (
                  <Box
                    sx={{
                      backgroundImage: item.imageSrc
                        ? `url('${item.imageSrc}')`
                        : 'none',
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center',
                      width: '100%',
                      height: 300,
                      mt: 4,
                      mb: 3,
                      mx: 'auto',
                      ...(item.imageSx || {}),
                    }}
                  />
                )}

                <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                  <Typography
                    variant="h3"
                    sx={{ textAlign: 'center', mb: 1.5 }}
                  >
                    {item.subtitle}
                  </Typography>

                  <Typography variant="body1" sx={{ textAlign: 'center' }}>
                    {item.content}
                  </Typography>
                </Box>
              </CarouselItem>
            )}
          />
        </DialogContent>

        <Stack
          alignItems="center"
          sx={{ pt: 2, pb: 2, gap: { xs: 0.5, sm: 1.5 } }}
        >
          <Box>
            <Button onClick={onSubscribe} variant="contained">
              <FormattedMessage
                id="premium.dialog.save"
                defaultMessage="Předplatit Infigy Plus"
              />
            </Button>
          </Box>
        </Stack>
      </Dialog>
    </>
  );
};

export const usePremiumDialog = () => {
  const [open, setOpen] = useState(false);

  return {
    dialog: <PremiumDialog open={open} onClose={() => setOpen(false)} />,
    open: () => setOpen(true),
    close: () => setOpen(false),
    isOpen: open,
  };
};
